import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {useQuery} from '@github-ui/react-query'
import {IssueHeaderRefetchServerQuery} from '@github-ui/server-persisted-queries'
import type {IssueHeaderRefetchServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueHeaderRefetchServerQuery'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {createContext, use, useMemo} from 'react'

import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import type {HeaderFragment} from './Header'

export function issueHeaderRefetchQueryKey(id: string) {
  return ['issue-header-refetch', id] as const
}

export const ISSUE_HEADER_LIVE_UPDATE_THROTTLE_MS = 250

type IssueHeaderAliveNotification = {
  wait?: number
  issue_title_updated?: true
  issue_state_updated?: true
  issue_transfer_state_updated?: true
  issue_dependencies_summary_updated?: true
  parent_issue_updated?: true
}

/** Mirrors the header keys the backend publishes; a ping carrying none of them belongs elsewhere on the page. */
const HEADER_UPDATE_KEYS = [
  'issue_title_updated',
  'issue_state_updated',
  'issue_transfer_state_updated',
  'issue_dependencies_summary_updated',
  'parent_issue_updated',
] as const satisfies ReadonlyArray<keyof IssueHeaderAliveNotification>

function isHeaderUpdated(data: IssueHeaderAliveNotification): boolean {
  return HEADER_UPDATE_KEYS.some(key => data[key] === true)
}

/** The subset of the header the endpoint returns, so it is merged over the prop rather than replacing it. */
export type IssueHeaderRefetchData = Pick<
  HeaderFragment,
  'id' | 'number' | 'title' | 'titleHTML' | 'url' | 'state' | 'stateReason' | 'viewerCanUpdateNext' | 'duplicateOf'
> &
  Pick<
    IssueSecondaryNode,
    'issueDependenciesSummary' | 'parent' | 'topBlockedBy' | 'topBlocking' | 'topRelatesTo' | 'isTransferInProgress'
  > & {
    readonly repository: Pick<
      HeaderFragment['repository'],
      'id' | 'name' | 'nameWithOwner' | 'isArchived' | 'isWritable' | 'owner'
    >
  }

type IssueHeaderRefetchResult = {
  issue?: IssueHeaderRefetchData
}

const EMPTY_TOP_BLOCKED_BY: IssueHeaderRefetchData['topBlockedBy'] = {
  nodes: [],
  pageInfo: {hasNextPage: false},
  totalCount: 0,
}
const EMPTY_TOP_BLOCKING: IssueHeaderRefetchData['topBlocking'] = {
  nodes: [],
  pageInfo: {hasNextPage: false},
  totalCount: 0,
}
const EMPTY_TOP_RELATES_TO: IssueHeaderRefetchData['topRelatesTo'] = {
  nodes: [],
  pageInfo: {hasNextPage: false},
  totalCount: 0,
}

export type IssueHeaderSeedData = Pick<
  IssueSecondaryNode,
  'issueDependenciesSummary' | 'parent' | 'topBlockedBy' | 'topBlocking' | 'topRelatesTo' | 'isTransferInProgress'
> &
  Partial<Pick<IssueSecondaryNode, 'title' | 'titleHTML'>>

async function fetchIssueHeader(id: string, signal?: AbortSignal): Promise<IssueHeaderRefetchResult> {
  const params = new URLSearchParams({id, queryId: IssueHeaderRefetchServerQuery})
  const response = await reactFetchJSON(`/issues/issue-header-refetch.json?${params}`, {signal})
  if (!response.ok) throw new Error(`Issue header refetch failed (${response.status})`)

  const json = (await response.json()) as {
    data?: IssueHeaderRefetchServerQueryQuery & {
      node?: IssueHeaderRefetchData & {__typename: 'Issue'}
    }
    errors?: unknown[]
  }
  if (!json.data) throw new Error('Issue header refetch query returned no data')

  const node = json.data.node
  return {issue: node?.__typename === 'Issue' ? node : undefined}
}

export const IssueHeaderDataContext = createContext<IssueHeaderRefetchData | null>(null)

export function useIssueHeaderDataContext(): IssueHeaderRefetchData {
  const data = use(IssueHeaderDataContext)
  if (!data) throw new Error('useIssueHeaderDataContext must be used within IssueHeaderDataContext')
  return data
}

/**
 * Owns the narrow header endpoint. Call this once per page: the sticky header and the header read
 * the same answer through the context, so a state or title change costs one request between them.
 */
export function useIssueHeaderRefetch({
  issue,
  headerSecondary,
  issueUpdatesChannel,
}: {
  issue: HeaderFragment
  headerSecondary?: IssueHeaderSeedData
  issueUpdatesChannel?: string | null
}): IssueHeaderRefetchData {
  const issueId = issue.id
  const headerQueryKey = useMemo(() => issueHeaderRefetchQueryKey(issueId), [issueId])
  useInvalidateOnAliveUpdate<IssueHeaderAliveNotification>(issueUpdatesChannel, headerQueryKey, {
    filter: isHeaderUpdated,
    throttleTimeout: ISSUE_HEADER_LIVE_UPDATE_THROTTLE_MS,
  })
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- This existing SPQ has no runtime schema and refreshes only after an Alive event.
  const headerQuery = useQuery({
    queryKey: headerQueryKey,
    queryFn: ({signal}) => fetchIssueHeader(issueId, signal),
    initialData: (): IssueHeaderRefetchResult => ({}),
    staleTime: Infinity,
  })
  const headerData = useMemo(() => {
    const refetchedIssue = headerQuery.data.issue
    const seed: IssueHeaderRefetchData = {
      id: issue.id,
      number: issue.number,
      title: headerSecondary?.title ?? issue.title,
      titleHTML: headerSecondary?.titleHTML ?? issue.titleHTML,
      url: issue.url,
      state: issue.state,
      stateReason: issue.stateReason,
      viewerCanUpdateNext: issue.viewerCanUpdateNext,
      duplicateOf: issue.duplicateOf,
      issueDependenciesSummary: headerSecondary?.issueDependenciesSummary ?? {blockedBy: 0, blocking: 0},
      parent: headerSecondary?.parent ?? null,
      topBlockedBy: headerSecondary?.topBlockedBy ?? EMPTY_TOP_BLOCKED_BY,
      topBlocking: headerSecondary?.topBlocking ?? EMPTY_TOP_BLOCKING,
      topRelatesTo: headerSecondary?.topRelatesTo ?? EMPTY_TOP_RELATES_TO,
      isTransferInProgress: headerSecondary?.isTransferInProgress ?? false,
      repository: {
        id: issue.repository.id,
        name: issue.repository.name,
        nameWithOwner: issue.repository.nameWithOwner,
        isArchived: issue.repository.isArchived,
        isWritable: issue.repository.isWritable,
        owner: issue.repository.owner,
      },
    }
    return refetchedIssue
      ? {...seed, ...refetchedIssue, repository: {...seed.repository, ...refetchedIssue.repository}}
      : seed
  }, [headerQuery.data.issue, headerSecondary, issue])

  return useOptimisticData(headerData, {targets: [{queryKey: headerQueryKey}]})
}
